import { OtherSettlementType } from '@/renderer/types/order';
import { SettleOrderModalProps, SettlementTab } from '@/renderer/types/sales';
import { Loader2, Search, UserRound, Wallet, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, DropdownSelect } from '../common';

const formatAmount = (value: number): string => value.toLocaleString();
// PENDING is deprecated — kept out of the UI but the type/RPC/thunk logic
// stays intact for a later removal stage.
const otherSettlementTypes: OtherSettlementType[] = ['CANCELLED', 'NC'];

export const SettleOrderModal: React.FC<SettleOrderModalProps> = ({
  isOpen,
  order,
  paymentMethods,
  clients,
  isPaymentMethodsLoading = false,
  isSubmitting = false,
  onClose,
  onSubmitDirect,
  onSubmitSplit,
  onSubmitCredit,
  onSubmitOther,
}) => {
  const [activeTab, setActiveTab] = useState<SettlementTab>('DIRECT');
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedOtherType, setSelectedOtherType] = useState<
    OtherSettlementType | ''
  >('');
  const [splitAmounts, setSplitAmounts] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState('');
  const [creditSearchQuery, setCreditSearchQuery] = useState('');
  const [splitError, setSplitError] = useState<string | null>(null);
  const [creditError, setCreditError] = useState<string | null>(null);
  const [otherError, setOtherError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setActiveTab('DIRECT');
    setNotes('');
    setCreditSearchQuery('');
    setSelectedOtherType('');
    setSplitError(null);
    setCreditError(null);
    setOtherError(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (paymentMethods.length === 0) {
      setSelectedPaymentMethodId('');
      return;
    }
    setSelectedPaymentMethodId((prev) => {
      if (prev && paymentMethods.some((method) => String(method.id) === prev)) {
        return prev;
      }
      return String(paymentMethods[0].id);
    });
  }, [isOpen, paymentMethods]);

  useEffect(() => {
    if (!isOpen) return;

    const preferredClientId = order?.client_id || '';
    if (
      preferredClientId &&
      clients.some((client) => client.id === preferredClientId)
    ) {
      setSelectedClientId(preferredClientId);
      return;
    }

    setSelectedClientId('');
  }, [clients, isOpen, order?.client_id]);

  useEffect(() => {
    if (!isOpen) return;
    setSplitAmounts(
      paymentMethods.reduce(
        (acc, method) => {
          acc[String(method.id)] = '';
          return acc;
        },
        {} as Record<string, string>,
      ),
    );
  }, [isOpen, paymentMethods]);

  const settlementAmount = useMemo(() => {
    if (!order) return 0;
    return Math.max(order.balance_amount ?? order.net_amount, 0);
  }, [order]);

  const filteredCreditClients = useMemo(() => {
    const query = creditSearchQuery.trim().toLowerCase();
    if (!query) return clients;

    return clients.filter((client) => {
      const name = client.full_name.toLowerCase();
      const email = client.email?.toLowerCase() || '';
      const contact = client.contact?.toLowerCase() || '';
      const address = client.address?.toLowerCase() || '';
      return (
        name.includes(query) ||
        email.includes(query) ||
        contact.includes(query) ||
        address.includes(query)
      );
    });
  }, [clients, creditSearchQuery]);

  const handleSubmitDirect = async () => {
    const paymentMethodId = Number(selectedPaymentMethodId);
    if (!paymentMethodId) return;

    await onSubmitDirect({
      paymentMethodId,
      notes,
    });
  };

  const splitTotal = useMemo(() => {
    return paymentMethods.reduce((total, method) => {
      const amount = Number(splitAmounts[String(method.id)] || 0);
      if (Number.isNaN(amount) || amount <= 0) return total;
      return total + amount;
    }, 0);
  }, [paymentMethods, splitAmounts]);

  const isSplitTotalValid =
    Math.round(splitTotal * 100) === Math.round(settlementAmount * 100);

  const handleSubmitSplit = async () => {
    const payments = paymentMethods
      .map((method) => ({
        paymentMethodId: method.id,
        amount: Number(splitAmounts[String(method.id)] || 0),
      }))
      .filter((payment) => !Number.isNaN(payment.amount) && payment.amount > 0);

    if (payments.length === 0) {
      setSplitError('Enter at least one payment amount');
      return;
    }

    if (!isSplitTotalValid) {
      setSplitError('Split amounts must match the exact bill total');
      return;
    }

    setSplitError(null);
    await onSubmitSplit({
      notes,
      payments,
    });
  };

  const handleSubmitCredit = async () => {
    if (!selectedClientId) {
      setCreditError('Select a client for credit settlement');
      return;
    }

    setCreditError(null);
    await onSubmitCredit({
      clientId: selectedClientId,
      notes,
    });
  };

  const handleSubmitOther = async () => {
    if (!selectedOtherType) {
      setOtherError('Select a settlement type');
      return;
    }

    if (!notes.trim()) {
      setOtherError('Settlement notes are required');
      return;
    }

    setOtherError(null);
    await onSubmitOther({
      notes,
      type: selectedOtherType,
    });
  };

  if (!isOpen || !order) return null;

  const isDirectSubmitDisabled =
    isSubmitting ||
    isPaymentMethodsLoading ||
    paymentMethods.length === 0 ||
    !selectedPaymentMethodId ||
    settlementAmount <= 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/45 backdrop-blur-[1px]"
        onClick={isSubmitting ? undefined : onClose}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !isSubmitting) onClose();
        }}
        role="button"
        tabIndex={0}
        aria-label="Close settle order modal"
      />

      <div className="relative w-full max-w-2xl overflow-hidden rounded-md bg-white text-black shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Settle Order
            </h2>
            <p className="text-sm text-gray-600">
              BILL #{order.bill_number} | Balance{' '}
              {formatAmount(settlementAmount)}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-60"
            aria-label="Close modal"
          >
            <X size={22} />
          </button>
        </div>

        <div className="space-y-4 px-6 pb-6 pt-5">
          <div className="grid grid-cols-4 gap-2">
            {(['DIRECT', 'SPLIT', 'CREDIT', 'OTHER'] as SettlementTab[]).map(
              (tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  disabled={isSubmitting}
                  className={`h-10 rounded border text-xs font-semibold uppercase tracking-wide transition-colors ${
                    activeTab === tab
                      ? 'border-primary bg-primary text-white'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {tab}
                </button>
              ),
            )}
          </div>

          {activeTab === 'DIRECT' ? (
            <>
              <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                <p>
                  Total amount:{' '}
                  <strong>{formatAmount(order.total_amount)}</strong>
                </p>
                <p>
                  Discount:{' '}
                  <strong>{formatAmount(order.discount_amount)}</strong>
                </p>
                <p>
                  Net: <strong>{formatAmount(order.net_amount)}</strong>
                </p>
                <p>
                  Amount to settle:{' '}
                  <strong>{formatAmount(settlementAmount)}</strong>
                </p>
              </div>

              <DropdownSelect
                label="Payment Mode"
                value={selectedPaymentMethodId}
                onChange={setSelectedPaymentMethodId}
                options={paymentMethods.map((pm) => ({
                  value: String(pm.id),
                  label: pm.name,
                }))}
                placeholder={
                  isPaymentMethodsLoading
                    ? 'Loading payment modes…'
                    : 'Select payment mode'
                }
                isLoadingOptions={isPaymentMethodsLoading}
                disabled={isSubmitting || isPaymentMethodsLoading}
                searchable={paymentMethods.length > 5}
              />

              <div>
                <p className="mb-1 text-sm font-semibold text-gray-800">
                  Notes (optional)
                </p>
                <textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  disabled={isSubmitting}
                  rows={3}
                  placeholder="Add settlement notes"
                  className="w-full resize-none rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
                />
              </div>

              <Button
                type="button"
                onClick={handleSubmitDirect}
                disabled={isDirectSubmitDisabled}
                className="h-10 text-xs font-semibold uppercase tracking-wide disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Settling
                  </>
                ) : (
                  <>
                    <Wallet size={14} />
                    Save Direct Settlement
                  </>
                )}
              </Button>
            </>
          ) : activeTab === 'SPLIT' ? (
            <>
              <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                <p>
                  Bill total: <strong>{formatAmount(settlementAmount)}</strong>
                </p>
                <p>
                  Entered total: <strong>{formatAmount(splitTotal)}</strong>
                </p>
              </div>

              <div className="space-y-2">
                {paymentMethods.map((paymentMethod) => (
                  <div
                    key={paymentMethod.id}
                    className="grid grid-cols-[140px_1fr] items-center gap-2"
                  >
                    <span className="text-xs font-semibold uppercase tracking-wide text-black/70">
                      {paymentMethod.name}
                    </span>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={splitAmounts[String(paymentMethod.id)] || ''}
                      onChange={(event) => {
                        const { value } = event.target;
                        setSplitAmounts((previous) => ({
                          ...previous,
                          [String(paymentMethod.id)]: value,
                        }));
                        setSplitError(null);
                      }}
                      disabled={isSubmitting}
                      placeholder="0"
                      className="h-10 w-full rounded border border-gray-300 bg-white px-3 text-sm text-gray-900 outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
                    />
                  </div>
                ))}
              </div>

              <div>
                <p className="mb-1 text-sm font-semibold text-gray-800">
                  Notes (optional)
                </p>
                <textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  disabled={isSubmitting}
                  rows={3}
                  placeholder="Add settlement notes"
                  className="w-full resize-none rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
                />
              </div>

              {splitError ? (
                <p className="text-xs font-medium text-red-600">{splitError}</p>
              ) : null}

              <Button
                type="button"
                onClick={handleSubmitSplit}
                disabled={isSubmitting || !isSplitTotalValid}
                className="h-10 text-xs font-semibold uppercase tracking-wide disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Saving
                  </>
                ) : (
                  'Save Split Settlement'
                )}
              </Button>
            </>
          ) : activeTab === 'CREDIT' ? (
            <>
              <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                <p>
                  Credit amount:{' '}
                  <strong>{formatAmount(settlementAmount)}</strong>
                </p>
                <p className="text-xs text-black/70">
                  Select the client account to settle this bill as CREDIT.
                </p>
              </div>

              <div className="relative">
                <Search
                  size={14}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  value={creditSearchQuery}
                  onChange={(event) => setCreditSearchQuery(event.target.value)}
                  placeholder="Search for a client"
                  className="h-10 w-full rounded border border-gray-300 bg-white pl-9 pr-3 text-sm text-black outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
                />
              </div>

              <div className="max-h-[220px] space-y-1 overflow-y-auto rounded border border-gray-200 bg-white">
                {filteredCreditClients.length === 0 ? (
                  <p className="p-4 text-center text-sm text-black/60">
                    No clients found
                  </p>
                ) : (
                  filteredCreditClients.map((client) => {
                    const isSelected = selectedClientId === client.id;
                    return (
                      <button
                        key={client.id}
                        type="button"
                        disabled={isSubmitting}
                        onClick={() => {
                          setSelectedClientId(client.id);
                          setCreditError(null);
                        }}
                        className={`flex w-full items-center justify-between border-b border-gray-200 px-3 py-2 text-left transition-colors last:border-b-0 ${
                          isSelected
                            ? 'app-bg border-primary'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <span className="rounded-full bg-[#eaf2f8] p-2 text-primary">
                            <UserRound size={14} />
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-black">
                              {client.full_name}
                            </p>
                            <p className="truncate text-xs text-black/70">
                              {client.email || client.contact || 'No contact'}
                            </p>
                          </div>
                        </div>
                        {client.menu_percentage_rate ? (
                          <span className="text-sm font-semibold text-black/80">
                            {client.menu_percentage_rate.toFixed(2)}x
                          </span>
                        ) : null}
                      </button>
                    );
                  })
                )}
              </div>

              <div>
                <p className="mb-1 text-sm font-semibold text-gray-800">
                  Notes (optional)
                </p>
                <textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  disabled={isSubmitting}
                  rows={3}
                  placeholder="Add credit settlement notes"
                  className="w-full resize-none rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
                />
              </div>

              {creditError ? (
                <p className="text-xs font-medium text-red-600">
                  {creditError}
                </p>
              ) : null}

              {selectedClientId ? (
                <Button
                  type="button"
                  onClick={handleSubmitCredit}
                  disabled={isSubmitting}
                  className="h-10 text-xs font-semibold uppercase tracking-wide disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Saving
                    </>
                  ) : (
                    'Save Credit Settlement'
                  )}
                </Button>
              ) : (
                <span className="text-xs font-medium uppercase tracking-wide text-black/60">
                  Select a client to continue
                </span>
              )}
            </>
          ) : (
            <>
              <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                <p className="text-xs text-black/70">
                  A client is not required for OTHER settlement, but a reason
                  must be entered below.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {otherSettlementTypes.map((option) => (
                  <button
                    key={option}
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => {
                      setSelectedOtherType(option);
                      setOtherError(null);
                    }}
                    className={`h-10 rounded border text-xs font-semibold uppercase tracking-wide ${
                      selectedOtherType === option
                        ? 'border-primary bg-primary text-white'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>

              <div>
                <p className="mb-1 text-sm font-semibold text-gray-800">
                  Notes
                </p>
                <textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  disabled={isSubmitting}
                  rows={3}
                  placeholder="Explain the reason for this settlement"
                  className="w-full resize-none rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
                />
              </div>

              {otherError ? (
                <p className="text-xs font-medium text-red-600">{otherError}</p>
              ) : null}

              <Button
                type="button"
                onClick={handleSubmitOther}
                disabled={isSubmitting || !selectedOtherType}
                className="h-10 text-xs font-semibold uppercase tracking-wide disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Saving
                  </>
                ) : (
                  'Save Other Settlement'
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
